export class IPSyntaxError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
  }
}
export class CIDRSyntaxError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
  }
}
export class IPv4 {
  readonly value: number;
  constructor(value: number) {
    // bitwise operations in JS return signed 32 bit numbers. For consistency,
    // we convert negative numbers back to positive.
    if (value < 0) value = 2 ** 32 + value;
    this.value = value;
  }

  equals(other: any): boolean {
    return other instanceof IPv4 && other.value === this.value;
  }

  toString(): string {
    let v = this.value;
    const groups = [];
    for (let i = 0; i !== 4; ++i) {
      groups.push(v & 0xff);
      v >>= 8;
    }
    return groups.reverse().join('.');
  }
}

export class IPv6 {
  constructor(readonly value: bigint) {}

  equals(other: any): boolean {
    return other instanceof IPv6 && other.value === this.value;
  }

  toString(): string {
    let v = this.value;
    let groups = [];
    for (let i = 0; i !== 8; ++i) {
      groups.push(v & 0xffffn);
      v >>= 16n;
    }

    groups = groups.reverse();

    // Find longest run of zeroes to replace with ::
    let longestRunStart = -1;
    let longestRunLength = 0;
    let thisRunStart = -1;
    let thisRunLength = 0;
    for (let i = 0; i !== 8; ++i) {
      if (groups[i] === 0n) {
        // 0, so continue or start run
        if (thisRunStart === -1) {
          // Start
          thisRunStart = i;
          thisRunLength = 0;
        }
        ++thisRunLength;
      }
      if (groups[i] !== 0n || i === 7) {
        if (thisRunLength > longestRunLength) {
          longestRunStart = thisRunStart;
          longestRunLength = thisRunLength;
        }
        thisRunStart = -1; // Run ended. Set up for next run
      }
    }

    const strGroups = groups.map((g) => g.toString(16));

    if (longestRunLength < 2) return strGroups.join(':');
    else {
      const group1 = strGroups.slice(0, longestRunStart);
      const group2 = strGroups.slice(longestRunStart + longestRunLength);
      return group1.join(':') + '::' + group2.join(':');
    }
  }
}

export function parseIpv4(ip: string): IPv4 {
  ip = ip.trim();
  const parts = ip.split('.');
  if (parts.length !== 4) throw new IPSyntaxError();
  if (parts.some((p) => !p.match(/^[0-9]+$/))) throw new IPSyntaxError();

  let result = 0;
  for (const part of parts) {
    result *= 256;
    const partValue = parseInt(part, 10);
    if (partValue >= 256)
      throw new IPSyntaxError(`Part value cannot be more than 255 but got ${part}`);
    result += parseInt(part, 10);
  }
  return new IPv4(result);
}

function hexListToNumber(items: string[], perItem: bigint): bigint {
  let result = BigInt(0);
  for (const hex of items) {
    result *= perItem;
    const partValue = BigInt(parseInt(hex, 16));
    if (partValue >= perItem)
      throw new IPSyntaxError(`Part value cannot be more than ${perItem} but got ${hex}`);
    result += partValue;
  }
  return result;
}

export function parseIpv6(ip: string): IPv6 {
  ip = ip.trim();
  const sections = ip.split('::');
  if (sections.length > 2) throw new IPSyntaxError('Can only use :: once');

  const groups1 = sections[0].split(':');
  const groups2 = sections[1]?.split(':') ?? [];

  if (sections.length === 1) {
    if (groups1.length !== 8) throw new IPSyntaxError('Not 8 groups separated by colons');
  } else {
    // if (groups1.length + groups2.length >= 7) throw new IPSyntaxError("Can't use :: with this number of groups");
    // Above check follows the standard, but we're going to be a bit more
    // lenient with what we accept. IPv6.toString() does follow the rule.
    if (groups1.length + groups2.length > 8)
      throw new IPSyntaxError("Can't use :: with this number of groups");
  }

  const sectionValues = [groups1, groups2].map((groups) => {
    if (groups.length === 1 && groups[0] === '') groups = [];
    groups.forEach((g) => {
      if (!g.match(/^[0-9a-fA-F]+$/)) throw new IPSyntaxError(`Invalid IPv6 group: '${g}'`);
    });
    return hexListToNumber(groups, 0x10000n);
  });

  return new IPv6(sectionValues[0] * 0x10000n ** (8n - BigInt(groups1.length)) + sectionValues[1]);
}

export function parseAnyIp(ipString: string | IPv4 | IPv6): IPv4 | IPv6 {
  if (ipString instanceof IPv4 || ipString instanceof IPv6) {
    return ipString;
  }

  try {
    return parseIpv4(ipString);
  } catch (e: any) {
    console.log(e);
  }
  try {
    return parseIpv6(ipString);
  } catch (e: any) {
    console.log(e);
  }

  throw new CIDRSyntaxError("Couldn't parse as IPv4 or IPv6 CIDR");
}

export class IPv4CIDR {
  constructor(
    readonly startIp: IPv4,
    readonly networkPrefix: number,
  ) {}

  equals(other: any): boolean {
    return (
      other instanceof IPv4CIDR &&
      other.startIp.equals(this.startIp) &&
      other.networkPrefix === this.networkPrefix
    );
  }

  get endIp() {
    return new IPv4(this.startIp.value + 2 ** (32 - this.networkPrefix));
  }

  toString(prefixOptional?: boolean) {
    if (prefixOptional && this.networkPrefix === 32) return this.startIp.toString();
    return this.startIp.toString() + '/' + this.networkPrefix;
  }

  contains(ip: IPv4) {
    return ip.value >= this.startIp.value && ip.value < this.endIp.value;
  }
}

export class IPv6CIDR {
  constructor(
    readonly startIp: IPv6,
    readonly networkPrefix: number,
  ) {}

  equals(other: any): boolean {
    return (
      other instanceof IPv6CIDR &&
      other.startIp.equals(this.startIp) &&
      other.networkPrefix === this.networkPrefix
    );
  }

  get endIp() {
    return new IPv6(this.startIp.value + 2n ** (128n - BigInt(this.networkPrefix)));
  }

  toString(prefixOptional?: boolean) {
    if (prefixOptional && this.networkPrefix === 128) return this.startIp.toString();
    return this.startIp.toString() + '/' + this.networkPrefix;
  }

  contains(ip: IPv6) {
    return ip.value >= this.startIp.value && ip.value < this.endIp.value;
  }
}

function parseOneCidr(cidrString: string, ipType: 'ipv4' | 'ipv6'): IPv4CIDR | IPv6CIDR {
  cidrString = cidrString.trim();

  const cidrParts = cidrString.split('/');
  if (cidrParts.length > 2) throw new CIDRSyntaxError('Multiple / in CIDR');

  const [ipStr, prefixStr] = cidrParts;
  let ip = ipType === 'ipv4' ? parseIpv4(ipStr) : parseIpv6(ipStr);

  const maxNetPrefix = ipType === 'ipv4' ? 32 : 128;

  let prefix: number;
  if (prefixStr === undefined) {
    prefix = maxNetPrefix;
  } else {
    if (!prefixStr.match(/^[0-9]+$/)) {
      throw new CIDRSyntaxError('Bad network prefix length');
    }
    prefix = parseInt(prefixStr, 10);
  }

  if (prefix < 0 || prefix > maxNetPrefix) throw new CIDRSyntaxError('Network prefix out of range');

  // Turn 192.168.2.16/24 into 192.168.2.0
  const hostMask = 2n ** BigInt(maxNetPrefix - prefix) - 1n;
  if (ip instanceof IPv4) {
    ip = new IPv4(ip.value & ~Number(hostMask));
  } else {
    ip = new IPv6(ip.value & ~hostMask);
  }

  if (ip instanceof IPv4) {
    return new IPv4CIDR(ip, prefix);
  } else {
    return new IPv6CIDR(ip, prefix);
  }

  throw Error('Unreachable');
}

export function parseIpv4Cidr(cidrString: string): IPv4CIDR {
  return parseOneCidr(cidrString, 'ipv4') as IPv4CIDR;
}

export function parseIpv6Cidr(cidrString: string): IPv6CIDR {
  return parseOneCidr(cidrString, 'ipv6') as IPv6CIDR;
}

export function parseAnyCidr(cidrString: string): IPv4CIDR | IPv6CIDR {
  try {
    return parseIpv4Cidr(cidrString);
  } catch (e: any) {
    console.log(e);
  }
  try {
    return parseIpv6Cidr(cidrString);
  } catch (e: any) {
    console.log(e);
  }

  throw new CIDRSyntaxError("Couldn't parse as IPv4 or IPv6 CIDR");
}
